import { Data, DataFilter, LabelsAndData } from '../models';

interface RestrictToTopDataOptions {
  groupNonDisplayedItemsIntoOther?: boolean;
  maxItems?: number;
  otherItemsLabel?: string;
}

interface OrderDataOptions {
  dataOrderAscending?: boolean;
  forceZeroValuesToBottom?: boolean;
}

class ChartDataUtil {
  public total(data: number[]): number {
    return data.reduce((a, b) => a + b, 0);
  }

  public orderData(labelsAndData: LabelsAndData, inOptions?: OrderDataOptions): LabelsAndData {
    const defaults: OrderDataOptions = {
      dataOrderAscending: false,
      forceZeroValuesToBottom: false,
    };
    const options = { ...defaults, ...inOptions };

    const sourceDataCopy = [...labelsAndData.data];
    const sourceLabelsCopy = [...labelsAndData.labels];

    const labels: string[] = [];
    const data: number[] = [];

    while (sourceDataCopy.length > 0) {
      const nextValueIndex = sourceDataCopy.indexOf(
        !options.dataOrderAscending ? Math.max(...sourceDataCopy) : Math.min(...sourceDataCopy),
      );
      data.push(...sourceDataCopy.splice(nextValueIndex, 1));
      labels.push(...sourceLabelsCopy.splice(nextValueIndex, 1));
    }

    if (options.dataOrderAscending && options.forceZeroValuesToBottom) {
      const lastIndex = data.lastIndexOf(0);
      if (lastIndex >= 0 && lastIndex < data.length - 1) {
        data.push(...data.splice(0, lastIndex + 1));
        labels.push(...labels.splice(0, lastIndex + 1));
      }
    }

    return { labels, data };
  }

  public filterData(input: LabelsAndData, filter?: DataFilter): LabelsAndData {
    if (!filter) {
      return input;
    }

    const labels: string[] = [];
    const data: number[] = [];

    for (let i = 0; i < input.data.length; i++) {
      if (filter(input.labels[i], input.data[i])) {
        labels.push(input.labels[i]);
        data.push(input.data[i]);
      }
    }

    return { labels, data };
  }

  public restrictDataToTopX(orderedData: LabelsAndData, inOptions: RestrictToTopDataOptions): LabelsAndData {
    const defaults: RestrictToTopDataOptions = {
      groupNonDisplayedItemsIntoOther: true,
      maxItems: 10,
      otherItemsLabel: 'Others',
    };
    const options = { ...defaults, ...inOptions };

    const totalItems = orderedData.labels.length;
    const maxItems = Number(options.maxItems);

    if (totalItems <= maxItems) {
      // no need to limit any items
      return orderedData;
    }

    const topData = {
      labels: orderedData.labels.splice(0, maxItems),
      data: orderedData.data.splice(0, maxItems),
    };

    if (options.groupNonDisplayedItemsIntoOther) {
      const othersTotal = orderedData.data.reduce((a, b) => a + b, 0);
      topData.labels.push(`${options.otherItemsLabel}`);
      topData.data.push(othersTotal);
    }

    return topData;
  }

  public filterDataSeries(data: Data, filter?: DataFilter): Data {
    const labelDataPairs = this.convertToLabelsAndData(data).map((pair) => this.filterData(pair, filter));

    // should be the same across all series, so just use the first one
    const labels = labelDataPairs.length ? labelDataPairs[0].labels : [];

    return {
      scale: {
        title: data.scale.title,
        labels,
      },
      series: labelDataPairs.map((pair, id) => {
        // assumes the order has stayed the same
        const series = data.series[id];
        return {
          name: series.name,
          displayName: series.name,
          data: pair.data,
        };
      }),
    };
  }

  private convertToLabelsAndData(data: Data): LabelsAndData[] {
    return data.series.map<LabelsAndData>((series) => ({
      labels: data.scale.labels,
      data: series.data,
    }));
  }
}

export const chartDataUtil = new ChartDataUtil();
